import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Chip from "@material-ui/core/Chip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { listSecurityEvents } from "../../services/securityAudit.api";

const ACCIONES = [
  "login", "logout", "refresh", "password_change", "user_create", "user_delete", "role_change",
  "permissions_change", "token_regenerate", "token_reveal", "token_auth", "rate_limit", "export", "audit_mode",
  "company_create", "company_delete", "security_alert"
];
const RESULTADOS = ["ok", "fallo", "bloqueado"];

const useStyles = makeStyles((theme) => ({
  mainPaper: { flex: 1, padding: theme.spacing(1), overflowY: "scroll", ...theme.scrollbarStyles },
  filtros: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1), alignItems: "center" },
  filtro: { minWidth: 160 },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13, margin: theme.spacing(0, 1, 1) },
  muted: { color: theme.palette.text.disabled, fontSize: 12 },
  alerta: { background: theme.palette.type === "dark" ? "rgba(244,67,54,0.12)" : "rgba(244,67,54,0.06)" },
  ok: { background: theme.palette.success ? theme.palette.success.main : "#2e7d32", color: "#fff" },
  fallo: { background: theme.palette.warning ? theme.palette.warning.main : "#ed6c02", color: "#fff" },
  bloqueado: { background: theme.palette.error.main, color: "#fff" }
}));

const fecha = (v) => {
  try { return new Date(v).toLocaleString(); } catch (e) { return v; }
};

/**
 * Seguridad (doc 0431, M-09): quién entró, quién cambió permisos o contraseñas, qué
 * token se usó y las alertas. El superadministrador ve todas las empresas; el
 * administrador, la suya. Solo lectura.
 */
const SecurityAudit = () => {
  const classes = useStyles();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isSuper, setIsSuper] = useState(false);
  const [filtros, setFiltros] = useState({ action: "", result: "", from: "", to: "" });

  const cargar = useCallback(async (pagina) => {
    setLoading(true);
    try {
      const params = { page: pagina, limit: 50 };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await listSecurityEvents(params);
      setEventos((prev) => (pagina === 1 ? data.events : [...prev, ...data.events]));
      setHasMore(Boolean(data.hasMore));
      setIsSuper(Boolean(data.isSuper));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { setPage(1); cargar(1); }, [cargar]);

  const cambiar = (k) => (e) => setFiltros((f) => ({ ...f, [k]: e.target.value }));
  const etiquetaAccion = (a) => i18n.t(`securityAudit.actions.${a}`, { defaultValue: a });
  const quien = (e) => e.actorName || e.actorLabel || (e.actorUserId ? `#${e.actorUserId}` : "—");

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("securityAudit.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filtros}>
            <TextField select size="small" variant="outlined" className={classes.filtro} label={i18n.t("securityAudit.filters.action")} value={filtros.action} onChange={cambiar("action")}>
              <MenuItem value="">{i18n.t("securityAudit.filters.all")}</MenuItem>
              {ACCIONES.map((a) => <MenuItem key={a} value={a}>{etiquetaAccion(a)}</MenuItem>)}
            </TextField>
            <TextField select size="small" variant="outlined" className={classes.filtro} label={i18n.t("securityAudit.filters.result")} value={filtros.result} onChange={cambiar("result")}>
              <MenuItem value="">{i18n.t("securityAudit.filters.all")}</MenuItem>
              {RESULTADOS.map((r) => <MenuItem key={r} value={r}>{i18n.t(`securityAudit.results.${r}`)}</MenuItem>)}
            </TextField>
            <TextField type="date" size="small" variant="outlined" label={i18n.t("securityAudit.filters.from")} InputLabelProps={{ shrink: true }} value={filtros.from} onChange={cambiar("from")} />
            <TextField type="date" size="small" variant="outlined" label={i18n.t("securityAudit.filters.to")} InputLabelProps={{ shrink: true }} value={filtros.to} onChange={cambiar("to")} />
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <div className={classes.subtitle}>{i18n.t(isSuper ? "securityAudit.subtitleSuper" : "securityAudit.subtitle")}</div>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("securityAudit.columns.date")}</TableCell>
              <TableCell>{i18n.t("securityAudit.columns.action")}</TableCell>
              <TableCell>{i18n.t("securityAudit.columns.result")}</TableCell>
              <TableCell>{i18n.t("securityAudit.columns.actor")}</TableCell>
              <TableCell>{i18n.t("securityAudit.columns.target")}</TableCell>
              <TableCell>{i18n.t("securityAudit.columns.ip")}</TableCell>
              {isSuper && <TableCell>{i18n.t("securityAudit.columns.company")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {eventos.map((e) => (
              <TableRow key={e.id} className={e.action === "security_alert" ? classes.alerta : undefined}>
                <TableCell>{fecha(e.createdAt)}</TableCell>
                <TableCell>{etiquetaAccion(e.action)}</TableCell>
                <TableCell><Chip size="small" label={i18n.t(`securityAudit.results.${e.result}`)} className={classes[e.result]} /></TableCell>
                <TableCell>{quien(e)}</TableCell>
                <TableCell>
                  {e.target || "—"}
                  {e.action === "security_alert" && e.details && e.details.descripcion && <div className={classes.muted}>{e.details.descripcion}</div>}
                </TableCell>
                <TableCell>{e.ip || "—"}</TableCell>
                {isSuper && <TableCell>{e.companyName || (e.companyId ? `#${e.companyId}` : i18n.t("securityAudit.platform"))}</TableCell>}
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={isSuper ? 7 : 6} />}
            {!loading && eventos.length === 0 && (
              <TableRow><TableCell colSpan={isSuper ? 7 : 6} className={classes.muted}>{i18n.t("securityAudit.empty")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {hasMore && !loading && (
          <Button fullWidth onClick={() => { const p = page + 1; setPage(p); cargar(p); }}>{i18n.t("securityAudit.loadMore")}</Button>
        )}
      </Paper>
    </MainContainer>
  );
};

export default SecurityAudit;
